import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import styled from 'styled-components';

import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { isType } from '@sorare/core/src/lib/gql';

import { Ranking } from 'components/leaderboard/RankRewards/Ranking';
import { CompactSo5Lineup } from 'components/lineup/CompactSo5Lineup';

import type {
  PastLeaderboardListQuery,
  PastLeaderboardListQueryVariables,
} from './__generated__/index.graphql';

const Wrapper = styled.div`
  border-radius: var(--double-unit);
  &.highlighted {
    border: 1px solid var(--c-blue-300);
  }
`;

const PAST_LEADERBOARD_LIST_QUERY = gql`
  query PastLeaderboardListQuery($userSlug: String!, $userGroupSlug: String!) {
    so5 {
      so5UserGroup(slug: $userGroupSlug) {
        slug
        topScoresCount
        membership(userSlug: $userSlug) {
          id
          ... on So5UserGroupMembership {
            id
            topSo5Rankings {
              id
              so5Lineup {
                id
                ...CompactSo5Lineup_so5Lineup
              }
              ...RankRewardsRanking_so5Ranking
            }
          }
        }
      }
    }
  }
  ${Ranking.fragments.so5Ranking}
  ${CompactSo5Lineup.fragments.so5Lineup}
` as TypedDocumentNode<
  PastLeaderboardListQuery,
  PastLeaderboardListQueryVariables
>;

type Props = {
  userSlug: string;
  expanded?: boolean;
  userGroupSlug: string;
  activeLineupId?: string;
};

export const PastLeaderboardList = ({
  userSlug,
  expanded,
  userGroupSlug,
  activeLineupId,
}: Props) => {
  const { data, loading } = useQuery(PAST_LEADERBOARD_LIST_QUERY, {
    skip: !expanded,
    variables: { userSlug, userGroupSlug },
  });

  if (loading) {
    return (
      <Vertical>
        <LoadingIndicator small />
      </Vertical>
    );
  }

  const { membership } = data?.so5.so5UserGroup || {};
  if (!isType(membership, 'So5UserGroupMembership')) {
    return null;
  }
  const { topSo5Rankings } = membership || {};

  if (!topSo5Rankings) {
    return null;
  }

  return (
    <Vertical>
      {topSo5Rankings.map(so5Ranking => (
        <Wrapper
          key={so5Ranking.id}
          className={classNames({
            highlighted: activeLineupId === so5Ranking.so5Lineup.id,
          })}
        >
          <Ranking so5Ranking={so5Ranking}>
            <CompactSo5Lineup so5Lineup={so5Ranking.so5Lineup} />
          </Ranking>
        </Wrapper>
      ))}
    </Vertical>
  );
};
